import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { environment } from '../../../environments/environment';
import { JwTokenModel, UserModel } from '../../core/api/models';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';
import { Utils } from '../../core/utils';
import { AlertService } from '../../shared/services/alert.service';
import { OfflineDataService } from '../../core/services/offline-data.service';
import { SendLogsService } from '../../core/services/send-logs.service';

@Component({
  selector: 'app-signin-component',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.css'],
  standalone: false,
})
export class SignInComponent implements OnInit, AfterViewInit {
  loginForm!: FormGroup;
  signedIn: boolean = false;
  loading: boolean = false;
  checkingCredential: boolean = false;
  authFailedError: string | undefined | null;

  token: string | undefined | null;
  returnUrl: string | undefined | null;

  bussinessLogo: string | null = null;
  bussinessName: string | null = 'ArkMed';

  loadingBusinessInfo: boolean = false;

  areThereUnsyncedData: boolean = false;
  isSyncing: boolean = false;

  private addSigninTrace(message: string, context?: Record<string, unknown>) {
    const ctx = context ? JSON.stringify(context) : undefined;
    this.sendLogsService.addDiagnostic(message, ctx);
  }

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
    private http: HttpClient,
    private alertService: AlertService,
    private offlineDataService: OfflineDataService,
    private sendLogsService: SendLogsService
  ) {}

  ngOnInit(): void {
    console.debug('SignInComponent.ngOnInit');

    this.offlineDataService.getOfflinePatients().subscribe({
      next: (patients) => {
        if (patients && patients.size > 0) {
          const offlinePatients = Array.from(patients.values()).map(
            (data) => data
          );
          const unsyncedPatients = offlinePatients.filter(
            (patient) =>
              patient.sessions.some(
                (session) => session.isDirty && (session.ID || 0) >= 0
              ) ||
              (patient.progressNotes &&
                Object.values(patient.progressNotes).some(
                  (note) => (note as any).isDirty
                ))
          );
          this.areThereUnsyncedData = unsyncedPatients.length > 0;
        } else {
          this.areThereUnsyncedData = false;
        }
      },
      error: (error) => {
        this.areThereUnsyncedData = false;
      },
    });

    this.route.queryParams.subscribe((params) => {
      this.token = params['token'];
      this.returnUrl = params['returnUrl'];

      if (this.token) console.debug('URL token', this.token);
      if (this.returnUrl) console.debug('URL returnUrl', this.returnUrl);
    });

    this.loginForm = this.formBuilder.group({
      username: ['', [Validators.required]],
      password: ['', [Validators.required]],
    });

    // get return url from route parameters or default to '/'

    if (this.authService.tokenInfo != null) {
      var jwToken = Utils.parseJwt(
        this.authService.tokenInfo.access_token || ''
      );
      console.info('access_token', jwToken);
      if (this.loginForm && this.loginForm.get('username'))
        this.loginForm
          .get('username')!
          .setValue(jwToken['unique_name'] || null);
    }
  }

  ngAfterViewInit(): void {
    console.debug('SignInComponent.AfterViewInit');

    this.loadingBusinessInfo = true;
    this.authService.settings().subscribe({
      next: (x) => {
        this.bussinessLogo = x?.BusinessLogoUrl || null;
        this.bussinessName = x?.BusinessFullName || 'ArkMed';
        if (x == null || !x.BusinessName) {
          //location.href = '_app_offline.htm';
        }
        this.loadingBusinessInfo = false;
      },
      error: (error) => {
        this.loadingBusinessInfo = false;
      },
    });

    this.token = this.token || this.route.snapshot.queryParamMap.get('token');
    this.addSigninTrace('signin-view-init', {
      hasTokenInQuery: !!this.token,
      hasReturnUrl: !!this.returnUrl,
      alreadySignedIn: this.authService.isSignedIn(),
    });

    if (this.authService.isSignedIn() && !this.token) {
      console.info('isSignedIn');
      this.addSigninTrace('already-signed-in-redirect', {
        returnUrl: this.returnUrl ?? '',
      });
      this.offlineDataService.checkAndSyncOfflineDataOnStartup();
      this.authService.redirectAfterLogin(null, this.returnUrl);
      return;
    }

    if (this.token) {
      this.authService.signOutNoRedirect().subscribe();

      this.authService.tokenInfo = {
        refresh_token: this.token,
        refresh_expires_at: '2099-01-01',
      };

      this.checkingCredential = true;
      this.addSigninTrace('token-handoff-started', {
        returnUrl: this.returnUrl ?? '',
      });
      console.debug(
        'Initial checking credential',
        this.authService.tokenInfo?.refresh_token
      );

      this.authService
        .getTokenInfo()
        .forEach((response) => {
          this.addSigninTrace('token-handoff-tokeninfo-success', {
            hasAccessToken: !!response?.access_token,
            hasRefreshToken: !!response?.refresh_token,
            expiresAt: response?.expires_at ?? '',
          });
          this.authService.getUserInfo().subscribe({
            next: (x) => {
              this.addSigninTrace('token-handoff-userinfo-success', {
                userId: x?.ID ?? 0,
                permissionsCount: x?.Permissions?.length ?? 0,
              });
              this.offlineDataService.checkAndSyncOfflineDataOnStartup();
              // Drop ?token= from history so WKWebView process reloads don't reuse a spent one-time token.
              this.authService.redirectAfterLogin(x, this.returnUrl);
            },
            error: (err) => {
              this.checkingCredential = false;
              this.loading = false;
              this.sendLogsService.addError(
                'Token handoff failed while loading user profile',
                err?.stack || JSON.stringify(err)
              );
              this.alertService.error(err?.Message || 'Unable to load user profile.');
            },
          });
        })
        .catch((err) => {
          this.sendLogsService.addError(
            'Token handoff failed on signin',
            err?.stack || JSON.stringify(err)
          );
          if (this.token)
            this.alertService.error(err.Message || 'Invalid token.');
        })
        .finally(() => {
          this.loading = false;
          this.checkingCredential = false;
        });
    }
  }

  public signIn(event?: Event) {
    event?.preventDefault();
    /*
    if (!this.loginForm.valid) {
      return;
    }
    */
    const userName = this.loginForm.get('username')?.value;
    const password = this.loginForm.get('password')?.value;
    this.loading = true;
    this.authFailedError = null;
    this.addSigninTrace('manual-signin-started', {
      hasUsername: !!userName,
      hasPassword: !!password,
      returnUrl: this.returnUrl ?? '',
    });
    this.authService
      .signIn(userName, password)
      .forEach((response) => {
        if (response.refresh_token) {
          // Keep the full token payload returned from Authenticate.
          // Overwriting with only refresh fields drops access_token/expires_at and can break subsequent API calls.
          this.authService.tokenInfo$.next(response);
          this.authService
            .getUserInfo()
            .forEach((x) => {
              this.addSigninTrace('manual-signin-success', {
                userId: x?.ID ?? 0,
                permissionsCount: x?.Permissions?.length ?? 0,
              });
              this.offlineDataService.checkAndSyncOfflineDataOnStartup();
              this.authService.redirectAfterLogin(x, this.returnUrl);
            })
            .catch((err) => {
              this.sendLogsService.addError(
                'Manual signin failed while loading user profile',
                err?.stack || JSON.stringify(err)
              );
              this.alertService.error(err.Message || 'Invalid token.');
              this.loading = false;
              this.checkingCredential = false;
            });
        } else {
          this.authService.getUserInfo().subscribe(
            (x) => {
              this.addSigninTrace('manual-signin-success-without-refresh', {
                userId: x?.ID ?? 0,
              });
              this.offlineDataService.checkAndSyncOfflineDataOnStartup();
              this.authService.redirectAfterLogin(x, this.returnUrl);
            },
            (err) => {
              this.sendLogsService.addError(
                'Manual signin credentials accepted but user profile failed',
                err?.stack || JSON.stringify(err)
              );
              this.alertService.error(err.Message || 'Invalid credentials');
              this.authFailedError = err.Message || 'Invalid credentials';
              this.loading = false;
              this.checkingCredential = false;
            }
          );
        }

      }).catch(
        err => {
          this.sendLogsService.addError(
            'Manual signin authenticate call failed',
            err?.stack || JSON.stringify(err)
          );
          this.alertService.error(err.Message || 'Invalid credentials');
          this.authFailedError = err.Message || 'Invalid credentials';
          this.loading = false;
        }).
      finally(() => {
        this.loading = false;
      });

  }


  goToOfflineData() {
    this.router.navigate(['/offline/patients']);
  }

  syncData(): void {
    this.isSyncing = true;
    this.offlineDataService.checkIsOnline().subscribe((isOnline) => {
      if (!isOnline) {
        this.isSyncing = false;
        this.alertService.error('Cannot sync patient data while offline');
        return;
      }

      this.offlineDataService
        .syncOfflineDataWithGroupReconciliation()
        .subscribe({
        next: (success) => {
          this.isSyncing = false;
          if (success) {
            this.alertService.success('Patient data synced successfully');
          } else {
            this.alertService.error('Failed to sync patient data');
            this.sendLogsService.addError('Failed to sync patient data');
          }
        },
        error: (error) => {
          this.isSyncing = false;
          this.alertService.error('Failed to sync patient data');
          this.sendLogsService.addError(
            'Failed to sync patient data',
            error?.stack
          );
        },
      });
    });
  }
  getSyncTooltip(): string {
    if (this.isSyncing) {
      return 'Syncing data...';
    }
    return 'Sync data';
  }
}
